/* Branding showcase on the public /demo page — real customer-branded
   Scan2Call QR posters (fetched from the backend, opaque ids only) padded
   with fictional samples up to 3 tiles, upselling the ₦30,000 one-time QR
   logo branding. Includes a deep-link CTA that opens the Brand-my-QR
   dialog on the dashboard (intent survives the login round-trip). */
import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { Building2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLES = [
  { name: "Zenith Estates", sector: "Real estate", logo: "/showcase/logo-zenith.png", sample: true },
  { name: "Havilah Hotels", sector: "Hospitality", logo: "/showcase/logo-havilah.png", sample: true },
  { name: "Crest Logistics", sector: "Logistics", logo: "/showcase/logo-crest.png", sample: true },
];

export const BrandingShowcase = ({ qrValue }) => {
  const [realLogos, setRealLogos] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/branding/showcase`);
        setRealLogos(
          (r.data?.logos || []).map((l) => ({
            name: l.name,
            sector: "Guard customer",
            logo: `${API}/doorbell/branding/showcase/${l.id}?v=${encodeURIComponent(l.v || "")}`,
            sample: false,
          }))
        );
      } catch { /* fall back to samples only */ }
    })();
  }, []);

  const tiles = [...realLogos, ...SAMPLES].slice(0, 3);
  const hasSamples = tiles.some((t) => t.sample);

  return (
    <div
      className="mt-5 bg-gray-950 border border-white/10 rounded-md p-4 sm:p-5"
      data-testid="branding-showcase"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Building2 className="w-4 h-4 text-amber-400" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300 font-body">
          For businesses
        </p>
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-2">
        Your logo. Your door. Your brand.
      </h2>
      <p className="text-xs text-gray-400 font-body mb-4">
        Corporate accounts can replace the Guard shield at the centre of their
        Scan2Call QR with their own logo <span className="text-gray-300">and</span> set a custom
        display name (e.g. &quot;Front Desk&quot;) shown to every visitor — a one-time{" "}
        <span className="text-amber-300 font-bold">&#8358;30,000</span> upgrade,
        done right from the app. Here&apos;s how it looks:
      </p>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {tiles.map((s) => (
          <div
            key={s.name + s.logo}
            className="bg-white rounded-md p-2 sm:p-3 text-center"
            data-testid={`branding-showcase-${s.sample ? "sample" : "live"}-${s.name.split(" ")[0].toLowerCase()}`}
          >
            <div className="flex items-center justify-center">
              <QRCodeCanvas
                value={qrValue}
                size={104}
                level="H"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#000000"
                imageSettings={{ src: s.logo, width: 30, height: 30, excavate: true, crossOrigin: "anonymous" }}
                className="w-full h-auto max-w-[104px]"
              />
            </div>
            <p className="mt-2 text-[10px] sm:text-[11px] font-body font-bold text-gray-900 leading-tight truncate">
              {s.name}
            </p>
            <p className={`text-[9px] font-body uppercase tracking-wider ${s.sample ? "text-gray-400" : "text-emerald-600 font-bold"}`}>
              {s.sector}
            </p>
          </div>
        ))}
      </div>

      <Link
        to="/dashboard?brandqr=1"
        onClick={() => { try { localStorage.setItem("guard.brandqr", "1"); } catch { /* private mode */ } }}
        className="mt-4 inline-flex w-full items-center justify-center gap-1.5 h-11 px-4 rounded-sm bg-amber-500 hover:bg-amber-400 text-black text-xs font-body font-bold uppercase tracking-widest transition-colors"
        data-testid="branding-showcase-cta"
      >
        Brand my QR now <ArrowRight className="w-3.5 h-3.5" />
      </Link>
      <p className="mt-2 text-[10px] text-gray-500 font-body italic">
        {hasSamples
          ? "Some posters are sample brands for illustration. "
          : "Real Guard customers, shown with their permission. "}
        Not signed up yet? The button walks you through sign-in first, then
        opens the logo upload.
      </p>
    </div>
  );
};

export default BrandingShowcase;
